import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Checkbox, ToggleButton, ToggleButtonGroup } from '@mui/material';
import TextField from '../text-field/TextField';
import Button from '../button/Button';
import IconButton from '../icon-button/IconButton';
import Typography from '../typography/Typography';
import { AddIcon } from '../../assets/icons/AddIcon';
import { DeleteIcon } from '../../assets/icons/DeleteIcon';
import { CableDirection, CableUnit } from '../../models/CableUnits';
import {
  createEmptyCableUnit,
  getCableDirectionArrow,
  isBlankCableUnit,
  parseCableUnits,
  serializeCableUnits
} from '../../utils/cableUnitsUtils';

interface Props {
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
}

const columnHeaders = ['Jord', 'Enhed', 'Retning', 'Enhed', 'Jord'];

const directionLabels: Record<CableDirection, string> = {
  [CableDirection.Left]: 'Retning venstre',
  [CableDirection.Both]: 'Retning begge veje',
  [CableDirection.Right]: 'Retning højre'
};

const CableUnitsField = (props: Props) => {
  const { label, value, onChange, readOnly, disabled, required, error } = props;
  const [rows, setRows] = useState<CableUnit[]>(parseCableUnits(value));
  const lastEmittedValue = useRef(value ?? '');

  // Blank rows are not part of the value, so only reset the rows when the value was changed from outside
  useEffect(() => {
    if ((value ?? '') !== lastEmittedValue.current) {
      lastEmittedValue.current = value ?? '';
      setRows(parseCableUnits(value));
    }
  }, [value]);

  const emit = (newRows: CableUnit[]) => {
    const newValue = serializeCableUnits(newRows) ?? '';
    if (newValue === lastEmittedValue.current) return;

    lastEmittedValue.current = newValue;
    onChange && onChange(newValue);
  };

  // Unit names are emitted on blur, everything else right away
  const updateRow = (index: number, change: Partial<CableUnit>, emitNow: boolean) => {
    const newRows = rows.map((row, i) => (i === index ? { ...row, ...change } : row));
    setRows(newRows);
    if (emitNow) emit(newRows);
  };

  const handleDeleteRow = (index: number) => {
    const newRows = rows.filter((_, i) => i !== index);
    setRows(newRows);
    emit(newRows);
  };

  const handleDirectionChange = (index: number, direction: CableDirection | null) => {
    // Clicking the selected direction again returns null; a cable always has a direction
    if (direction) updateRow(index, { direction }, true);
  };

  const renderEarth = (index: number, earth: boolean, field: 'leftEarth' | 'rightEarth') =>
    readOnly ? (
      <Typography>{earth ? 'Ja' : 'Nej'}</Typography>
    ) : (
      <StyledCheckbox
        checked={earth}
        disabled={disabled}
        inputProps={{ 'aria-label': field === 'leftEarth' ? 'Jord (venstre)' : 'Jord (højre)' }}
        onChange={(e) => updateRow(index, { [field]: e.target.checked }, true)}
      />
    );

  const renderUnit = (index: number, unit: string, field: 'leftUnit' | 'rightUnit') =>
    readOnly ? (
      <Typography>{unit}</Typography>
    ) : (
      <StyledTextField
        compact
        fullWidth
        variant="outlined"
        value={unit}
        disabled={disabled}
        error={error}
        inputProps={{ 'aria-label': field === 'leftUnit' ? 'Enhed (venstre)' : 'Enhed (højre)' }}
        onChange={(e) => updateRow(index, { [field]: e.target.value }, false)}
        onBlur={() => emit(rows)}
      />
    );

  const renderDirection = (index: number, direction: CableDirection) =>
    readOnly ? (
      <Direction>{getCableDirectionArrow(direction)}</Direction>
    ) : (
      <ToggleButtonGroup
        exclusive
        size="small"
        value={direction}
        disabled={disabled}
        onChange={(_, newDirection) => handleDirectionChange(index, newDirection)}
      >
        {[CableDirection.Left, CableDirection.Both, CableDirection.Right].map((d) => (
          <StyledToggleButton key={d} value={d} aria-label={directionLabels[d]}>
            {getCableDirectionArrow(d)}
          </StyledToggleButton>
        ))}
      </ToggleButtonGroup>
    );

  const visibleRows = readOnly ? rows.filter((row) => !isBlankCableUnit(row)) : rows;

  return (
    <Container>
      {readOnly ? (
        <ReadOnlyLabel>{label}</ReadOnlyLabel>
      ) : (
        <Typography variant="h5" fontWeight="bold" color={error ? 'error' : 'textPrimary'}>
          {`${label}${required ? '*' : ''}`}
        </Typography>
      )}
      {visibleRows.length > 0 && (
        <Grid readOnly={readOnly}>
          {columnHeaders.map((header, i) => (
            <ColumnHeader key={i}>{header}</ColumnHeader>
          ))}
          {!readOnly && <div />}
          {visibleRows.map((row, index) => (
            <Row key={index}>
              <Cell>{renderEarth(index, row.leftEarth, 'leftEarth')}</Cell>
              <Cell>{renderUnit(index, row.leftUnit, 'leftUnit')}</Cell>
              <Cell>{renderDirection(index, row.direction)}</Cell>
              <Cell>{renderUnit(index, row.rightUnit, 'rightUnit')}</Cell>
              <Cell>{renderEarth(index, row.rightEarth, 'rightEarth')}</Cell>
              {!readOnly && (
                <Cell>
                  <IconButton onClick={() => handleDeleteRow(index)} disabled={disabled} aria-label="Slet enhed">
                    <DeleteIcon size="18px" />
                  </IconButton>
                </Cell>
              )}
            </Row>
          ))}
        </Grid>
      )}
      {readOnly && visibleRows.length === 0 && <Typography>Ingen enheder</Typography>}
      {!readOnly && (
        <AddButton>
          <Button
            variant="secondary"
            compact
            square={false}
            disabled={disabled}
            onClick={() => setRows([...rows, createEmptyCableUnit()])}
          >
            <AddIcon size="16px" /> Tilføj enhed
          </Button>
        </AddButton>
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  row-gap: ${(props) => props.theme.spacing(2)};
`;

const ReadOnlyLabel = styled.span`
  color: ${(props) => props.theme.palette.grey.black40};
  font-size: 14px;
`;

const Grid = styled.div<{ readOnly?: boolean }>`
  display: grid;
  grid-template-columns: ${(props) =>
    props.readOnly ? 'auto minmax(0, 1fr) auto minmax(0, 1fr) auto' : 'auto minmax(0, 1fr) auto minmax(0, 1fr) auto auto'};
  align-items: center;
  column-gap: ${(props) => props.theme.spacing(2)};
  row-gap: ${(props) => props.theme.spacing(1)};
`;

// Lets each row's cells take part in the parent grid
const Row = styled.div`
  display: contents;
`;

const Cell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
`;

const ColumnHeader = styled(Typography)`
  font-weight: bold;
  text-align: center;
`;

const Direction = styled(Typography)`
  font-size: 1.25rem;
`;

const StyledTextField = styled(TextField)`
  background-color: ${(props) => props.theme.palette.background.primary};
`;

const StyledToggleButton = styled(ToggleButton)`
  min-width: 36px;
  font-size: 1.1rem;
  line-height: 1;
`;

const StyledCheckbox = styled(Checkbox)`
  &.Mui-checked {
    color: ${(props) => props.theme.palette.grey.black90};
  }
`;

const AddButton = styled.div`
  display: flex;
`;

export default CableUnitsField;
